import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Empty, Form, message, Pagination, Row, Select, Space, Table, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx-js-style';
import CompanyService, { CompanyServiceHistoryGroup, CompanyServiceHistoryItem } from '../../services/CompanyService';
import ProposalService from '../../services/ProposalService';
import { PageableResponse } from '../../types/pagination';

const { RangePicker } = DatePicker;

type AttributeOption = {
  id: number;
  name: string;
};

type ProposalOption = {
  id: number;
  proposalNumber: string;
  attributes: AttributeOption[];
};

type FilterValues = {
  proposalIds?: number[];
  attributeId?: number;
  controlDateRange?: [Dayjs, Dayjs];
  nextControlDateRange?: [Dayjs, Dayjs];
};

type CompanyServicesProps = {
  companyId?: number | string | null;
};

const toDateParam = (value?: Dayjs) => value?.format('YYYY-MM-DD');

export const formatCompanyServiceDate = (value?: string | null) => (
  value ? dayjs(value).format('DD/MM/YYYY') : '-'
);

export const buildCompanyServiceFilterQueryParams = (filters: FilterValues) => {
  const params: Record<string, string | number> = {};
  if (filters.proposalIds?.length) {
    params['proposalId.in'] = filters.proposalIds.join(',');
  }
  if (filters.attributeId) {
    params['attributeId.equals'] = filters.attributeId;
  }
  if (filters.controlDateRange?.[0]) {
    params['controlDate.greaterThanOrEqual'] = toDateParam(filters.controlDateRange[0])!;
  }
  if (filters.controlDateRange?.[1]) {
    params['controlDate.lessThanOrEqual'] = toDateParam(filters.controlDateRange[1])!;
  }
  if (filters.nextControlDateRange?.[0]) {
    params['nextControlDate.greaterThanOrEqual'] = toDateParam(filters.nextControlDateRange[0])!;
  }
  if (filters.nextControlDateRange?.[1]) {
    params['nextControlDate.lessThanOrEqual'] = toDateParam(filters.nextControlDateRange[1])!;
  }
  return params;
};

const extractProposalOptions = (payload: any): ProposalOption[] => {
  const proposals = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  return proposals
    .map((proposal: any) => {
      const id = Number(proposal?.id);
      if (!Number.isFinite(id) || id <= 0) {
        return null;
      }
      const attributes = new Map<number, AttributeOption>();
      (Array.isArray(proposal?.attributes) ? proposal.attributes : []).forEach((attribute: any) => {
        const attributeId = Number(attribute?.attributeId ?? attribute?.id);
        if (!Number.isFinite(attributeId) || attributeId <= 0) {
          return;
        }
        const name = String(attribute?.attributeName ?? attribute?.name ?? '').trim() || `Parametre #${attributeId}`;
        attributes.set(attributeId, { id: attributeId, name });
      });
      return {
        id,
        proposalNumber: String(proposal?.proposalNumber ?? `Teklif #${id}`).trim() || `Teklif #${id}`,
        attributes: Array.from(attributes.values()).sort((left, right) => left.name.localeCompare(right.name, 'tr')),
      };
    })
    .filter(Boolean)
    .sort((left: ProposalOption, right: ProposalOption) => right.proposalNumber.localeCompare(left.proposalNumber, 'tr')) as ProposalOption[];
};

const CompanyServices: React.FC<CompanyServicesProps> = ({ companyId }) => {
  const [form] = Form.useForm<FilterValues>();
  const [groups, setGroups] = useState<CompanyServiceHistoryGroup[]>([]);
  const [proposalOptions, setProposalOptions] = useState<ProposalOption[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const { current: currentPage, pageSize } = pagination;

  const selectedProposalIds = Form.useWatch('proposalIds', form);
  const attributeOptions = useMemo(() => {
    const selectedIds = Array.isArray(selectedProposalIds) ? selectedProposalIds.map(Number).filter(Boolean) : [];
    const source = selectedIds.length
      ? proposalOptions.filter((proposal) => selectedIds.includes(proposal.id))
      : proposalOptions;
    const attributes = new Map<number, AttributeOption>();
    source.forEach((proposal) => proposal.attributes.forEach((attribute) => attributes.set(attribute.id, attribute)));
    return Array.from(attributes.values()).sort((left, right) => left.name.localeCompare(right.name, 'tr'));
  }, [proposalOptions, selectedProposalIds]);

  const queryParams = useMemo(() => ({
    ...buildCompanyServiceFilterQueryParams(filters),
    page: currentPage - 1,
    size: pageSize,
  }), [filters, currentPage, pageSize]);

  const fetchGroups = useCallback(async () => {
    if (!companyId) {
      setGroups([]);
      setPagination((current) => ({ ...current, total: 0 }));
      return;
    }
    setLoading(true);
    try {
      const response = await CompanyService.getServiceHistoryGroups(companyId, queryParams);
      const page = response.data as PageableResponse<CompanyServiceHistoryGroup>;
      setGroups(Array.isArray(page?.content) ? page.content : []);
      setPagination((current) => ({ ...current, total: Number(page?.totalElements ?? 0) }));
    } catch (error) {
      console.error('Firma hizmetleri yüklenemedi:', error);
      setGroups([]);
      setPagination((current) => ({ ...current, total: 0 }));
      message.error('Firma hizmetleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [companyId, queryParams]);

  useEffect(() => {
    if (!companyId) {
      return;
    }
    ProposalService.getAllWithoutPagination({ companyId, statuIds: '4' })
      .then((response) => setProposalOptions(extractProposalOptions(response?.data)))
      .catch((error) => {
        console.error('Teklif seçenekleri yüklenemedi:', error);
        setProposalOptions([]);
      });
  }, [companyId]);

  useEffect(() => {
    const selectedAttributeId = form.getFieldValue('attributeId');
    if (selectedAttributeId && !attributeOptions.some((attribute) => attribute.id === Number(selectedAttributeId))) {
      form.setFieldsValue({ attributeId: undefined });
    }
  }, [attributeOptions, form]);

  useEffect(() => {
    void fetchGroups();
  }, [fetchGroups]);

  const handleFilter = (values: FilterValues) => {
    setPagination((current) => ({ ...current, current: 1 }));
    setFilters(values);
  };

  const handleReset = () => {
    form.resetFields();
    setPagination((current) => ({ ...current, current: 1 }));
    setFilters({});
  };

  const handleExport = async () => {
    if (!companyId) {
      return;
    }
    setExportLoading(true);
    try {
      const response = await CompanyService.exportServiceHistoryGroups(
        companyId,
        buildCompanyServiceFilterQueryParams(filters),
      );
      const exportGroups = Array.isArray(response.data) ? response.data : [];
      const headerRow = [
        { v: 'Teklif No', s: { font: { bold: true } } },
        { v: 'Hizmet/Parametre', s: { font: { bold: true } } },
        { v: 'Adet', s: { font: { bold: true } } },
        { v: 'Kontrol Tarihi', s: { font: { bold: true } } },
        { v: 'Bir Sonraki Kontrol Tarihi', s: { font: { bold: true } } },
      ];
      const rows = exportGroups.flatMap((group) => (group.services ?? []).map((service) => [
        group.proposalNumber ?? '-',
        service.attributeName ?? '-',
        service.quantity ?? '-',
        formatCompanyServiceDate(service.controlDate),
        formatCompanyServiceDate(service.nextControlDate),
      ]));
      const worksheet: any = XLSX.utils.aoa_to_sheet([headerRow, ...rows]) || {};
      worksheet['!cols'] = [{ wch: 18 }, { wch: 42 }, { wch: 10 }, { wch: 18 }, { wch: 28 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Hizmetler');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });
      saveAs(blob, `firma-hizmetleri-${companyId}.xlsx`);
    } catch (error) {
      console.error('Firma hizmetleri Excel dışa aktarımı başarısız:', error);
      message.error('Firma hizmetleri Excel dışa aktarımı başarısız oldu.');
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: 'Hizmet/Parametre',
      dataIndex: 'attributeName',
      key: 'attributeName',
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: 'Adet',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center' as const,
      width: 100,
      render: (value: number | null | undefined) => value ?? '-',
    },
    {
      title: 'Kontrol Tarihi',
      dataIndex: 'controlDate',
      key: 'controlDate',
      width: 160,
      render: (value: string | null | undefined) => formatCompanyServiceDate(value),
    },
    {
      title: 'Bir Sonraki Kontrol Tarihi',
      dataIndex: 'nextControlDate',
      key: 'nextControlDate',
      width: 220,
      render: (value: string | null | undefined) => formatCompanyServiceDate(value),
    },
  ];

  return (
    <div data-testid='company-services'>
      <Form form={form} layout='vertical' onFinish={handleFilter}>
        <Row gutter={[16, 0]} align='bottom'>
          <Col xs={24} md={12} lg={6}>
            <Form.Item label='Teklif' name='proposalIds'>
              <Select
                mode='multiple'
                allowClear
                showSearch
                optionFilterProp='label'
                placeholder='Teklif seçiniz'
                options={proposalOptions.map((proposal) => ({ value: proposal.id, label: proposal.proposalNumber }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Form.Item label='Parametre' name='attributeId'>
              <Select
                allowClear
                showSearch
                optionFilterProp='label'
                placeholder='Parametre seçiniz'
                options={attributeOptions.map((attribute) => ({ value: attribute.id, label: attribute.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Form.Item label='Kontrol Tarihi' name='controlDateRange'>
              <RangePicker className='w-full' format='DD/MM/YYYY' placeholder={['Başlangıç', 'Bitiş']} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Form.Item label='Bir Sonraki Kontrol Tarihi' name='nextControlDateRange'>
              <RangePicker className='w-full' format='DD/MM/YYYY' placeholder={['Başlangıç', 'Bitiş']} />
            </Form.Item>
          </Col>
        </Row>
        <Space wrap className='mb-4'>
          <Button type='primary' htmlType='submit'>Filtrele</Button>
          <Button onClick={handleReset}>Temizle</Button>
          <Tooltip title='Excel olarak indir'>
            <Button icon={<DownloadOutlined />} loading={exportLoading} onClick={handleExport}>
              Excel İndir
            </Button>
          </Tooltip>
        </Space>
      </Form>

      {groups.length > 0 ? (
        <div className='flex w-full flex-col gap-4'>
          {groups.map((group) => (
            <Card key={group.groupKey} size='small' title={group.proposalNumber || 'Teklif'}>
              <Table<CompanyServiceHistoryItem>
                columns={columns}
                dataSource={group.services ?? []}
                rowKey={(record) => String(record.taskId ?? `${record.proposalId}-${record.attributeId}-${record.controlDate}`)}
                loading={loading}
                pagination={false}
                scroll={{ x: 760 }}
              />
            </Card>
          ))}
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            onChange={(current, pageSize) => setPagination((value) => ({ ...value, current, pageSize }))}
          />
        </div>
      ) : (
        <Card loading={loading}>
          <Empty description='Tamamlanan hizmet bulunamadı.' />
        </Card>
      )}
    </div>
  );
};

export default CompanyServices;
